// services/chatConfigService.js
class ChatConfigService {
  constructor() {
    this.baseURL = `${process.env.REACT_APP_API_URL}/pdfs`;
  }

  // Helper method to get auth token
  async getAuthToken() {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.warn('⚠️ No authentication token found');
        throw new Error('User not authenticated');
      }
      return token;
    } catch (error) {
      console.error('❌ Failed to get auth token:', error);
      throw error;
    }
  }

  // Generic authenticated request method
  async makeAuthenticatedRequest(url, options = {}) {
    try {
      const token = await this.getAuthToken();
      
      const defaultOptions = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        ...options
      };

      console.log('📤 Making authenticated request to:', url);
      
      const response = await fetch(url, defaultOptions);

      if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        throw new Error('Authentication failed. Please login again.');
      }

      if (response.status === 429) {
        const errorData = await response.json().catch(() => ({}));
        const retryAfter = errorData.retryAfter || 60;
        
        const rateLimitError = new Error('Rate limit exceeded');
        rateLimitError.status = 429;
        rateLimitError.retryAfter = retryAfter;
        rateLimitError.details = errorData;
        throw rateLimitError;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error (${response.status}): ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Authenticated request failed:', error);
      throw error;
    }
  }

  // Process API response to create message data
  processApiResponse(apiResponse, originalMessage, source = 'chat_box') {
    if (!apiResponse.success) {
      return {
        isError: true,
        text: apiResponse.message || 'An error occurred',
        structured: null
      };
    }

    if (apiResponse.data?.structured) {
      const structured = apiResponse.data.structured;
      return {
        text: apiResponse.data.response || structured.answer || '',
        structured: structured,
        confidence: structured.confidence || 0.5,
        summary: structured.summary || '',
        sources: structured.sources || [],
        needsClarification: structured.needsClarification || false,
        isStructured: true
      };
    }
    
    return {
      text: apiResponse.data?.response || apiResponse.chatResponse || 'No response received',
      structured: null,
      isStructured: false
    };
  }

  async sendChatMessage(messageText, context = {}) {
  try {
    console.log('📤 Sending chat message to API:', { 
      messageText: messageText?.substring(0, 50) + '...',
      selectedModel: context.selectedModel,
      currentPage: context.currentPage || 1
    });
    
    const result = await this.makeAuthenticatedRequest(
      `${this.baseURL}/chat/message`,
      {
        method: 'POST',
        body: JSON.stringify({
          message: messageText,
          context: {
            ...context,
            selectedModel: context.selectedModel,
            timestamp: new Date().toISOString(),
            source: 'chat_box',
            responseFormat: 'structured',
            currentPage: context.currentPage || 1
          }
        })
      }
    );
    
    const processedResponse = this.processApiResponse(result, messageText, 'chat_box');
    return { ...result, processed: processedResponse };
    
  } catch (error) {
    console.error('❌ Failed to send chat message:', error);
    if (error.status === 429) {
      return {
        success: false, error: 'rate_limit_exceeded', message: '⏳ Rate limit exceeded.',
        retryAfter: error.retryAfter, timestamp: new Date().toISOString(),
        chatResponse: '⏳ Rate limit exceeded.',
        processed: { isError: true, text: '⏳ Rate limit exceeded.' }
      };
    }
    return { 
      success: false, error: error.message, requiresAuth: error.message.includes('Authentication'),
      message: `Connection error.`, chatResponse: `Connection error.`,
      processed: { isError: true, text: `Connection error.` }
    };
  }
}

async sendPdfText(selectedText, contextBefore, contextAfter, context = {}) {
  try {
    console.log('📤 Sending PDF text:', { 
      selectedText: selectedText?.substring(0, 50) + '...',
      currentPage: context.currentPage || 1
    });
    
    const result = await this.makeAuthenticatedRequest(
      `${this.baseURL}/chat/message`,
      {
        method: 'POST',
        body: JSON.stringify({
          message: selectedText,
          context: {
            ...context,
            contextBefore, 
            contextAfter,
            selectedModel: context.selectedModel,
            source: 'pdftext',
            timestamp: new Date().toISOString(),
            responseFormat: 'structured',
            currentPage: context.currentPage || 1
          }
        })
      }
    );
    
    const processedResponse = this.processApiResponse(result, selectedText, 'pdftext');
    return { ...result, processed: processedResponse };
    
  } catch (error) {
    console.error('❌ Failed to send PDF text:', error);
    if (error.status === 429) {
      return {
        success: false, error: 'rate_limit_exceeded', message: '⏳ Rate limit exceeded.',
        retryAfter: error.retryAfter, timestamp: new Date().toISOString(),
        chatResponse: '⏳ Rate limit exceeded.',
        processed: { isError: true, text: '⏳ Rate limit exceeded.' }
      };
    }
    return { 
      success: false, error: error.message, requiresAuth: error.message.includes('Authentication'),
      message: `Connection error.`, chatResponse: `Connection error.`,
      processed: { isError: true, text: `Connection error.` }
    };
  }
}

  async notifyPdfLoaded(documentName, source = 'pdf_render') {
    try {
      const result = await this.makeAuthenticatedRequest(
        `${this.baseURL}/chat/pdf-loaded`,
        {
          method: 'POST',
          body: JSON.stringify({ documentName, timestamp: new Date().toISOString(), source })
        }
      );
      let processedResponse = {
        text: result.data?.summary || result.chatResponse || "Good luck reading/studying! 📚",
        structured: null, isStructured: false
      };
      return { ...result, processed: processedResponse };
    } catch (error) {
      console.error('❌ Failed to notify PDF load:', error);
      return { success: false, error: error.message, processed: { text: "Good luck reading/studying! 📚" } };
    }
  }

  async notifyConfigChange(eventType, data) {
    try {
      // console.log('📤 Sending config change to API:', { eventType, data });
      const result = await this.makeAuthenticatedRequest(
        `${this.baseURL}/chat/config`,
        {
          method: 'POST',
          body: JSON.stringify({ eventType, data, timestamp: new Date().toISOString() })
        }
      );
      return result;
    } catch (error) {
      console.error('❌ Failed to notify config change:', error);
      return { success: false, error: error.message };
    }
  }

  async getQueries() {
    try {
      return await this.makeAuthenticatedRequest(`${this.baseURL}/chat/queries`, { method: 'GET' });
    } catch (error) {
      return { success: false, error: error.message, queries: [] };
    }
  }

  async getLLMs() {
    try {
      return await this.makeAuthenticatedRequest(`${this.baseURL}/chat/llms`, { method: 'GET' });
    } catch (error) {
      return { success: false, error: error.message, llms: [] };
    }
  }

  async getState() {
    try {
      return await this.makeAuthenticatedRequest(`${this.baseURL}/chat/state`, { method: 'GET' });
    } catch (error) {
      return { success: false, error: error.message, state: null };
    }
  }

  async getConversationHistory(pdfTitle) {
    try {
      return await this.makeAuthenticatedRequest(
        `${this.baseURL}/chat/history?pdfTitle=${encodeURIComponent(pdfTitle)}`,
        { method: 'GET' }
      );
    } catch (error) {
      return { success: false, error: error.message, history: [] };
    }
  }

  // --- Configuration Helpers ---

  async notifyLanguageChanged(languageName) {
    return this.notifyConfigChange('language_changed', { languageName });
  }

  async notifyLlmChanged(llmName, modelId = null) {
    const data = { llmName };
    if (modelId) data.modelId = modelId;
    return this.notifyConfigChange('llm_changed', data);
  }

  async notifyModelChanged(modelId, modelName) {
    return this.notifyConfigChange('model_changed', { modelId, modelName });
  }

  async notifyQueryChanged(queryKey) {
    return this.notifyConfigChange('query_changed', { queryId: queryKey, queryKey });
  }

  // FIXED: Ensure queryId is passed to payload
  async notifyCustomQueryCreated(queryName, queryText, queryId = null) {
    return this.notifyConfigChange('custom_query_created', {
      queryId: queryId || queryName,
      queryName,
      queryText
    });
  }

  // FIXED: Added queryId parameter and added it to payload object
  async notifyCustomQueryEdited(queryName, oldText, newText, queryId) {
    return this.notifyConfigChange('custom_query_edited', { 
      queryId,    // <--- THIS WAS MISSING
      queryName, 
      oldText, 
      newText 
    });
  }

  // FIXED: Clarified that we are sending the ID
  async notifyCustomQueryDeleted(queryId) {
    return this.notifyConfigChange('custom_query_deleted', { 
      queryId // <--- Ensure backend receives 'queryId'
    });
  }

  async notifyDocumentChanged(documentName) {
    return this.notifyConfigChange('document_changed', { documentName });
  }

  async testConnection() {
    try {
      return await this.makeAuthenticatedRequest(`${this.baseURL}/chat/test`);
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

const chatConfigService = new ChatConfigService();
export default chatConfigService;